import { type Atom, atom } from "jotai";
import { atomFamily, splitAtom, waitForAll } from "jotai/utils";
import { RedirectError, coerceBoolean, rethrowUnless } from "./utils";

import type { Layer } from "deck.gl";
import type { PrimitiveAtom } from "jotai";
import type { AtomFamily } from "jotai/vanilla/utils/atomFamily";
import type { Matrix4 } from "math.gl";
import type * as zarr from "zarrita";
import type { ZarrPixelSource } from "./ZarrPixelSource";
import { createSourceData, initLayerStateFromSource } from "./io";

import { GridLayer, type GridLayerProps, type GridLoader } from "./layers/grid-layer";
import { LabelLayer, type LabelLayerProps, type OmeColor } from "./layers/label-layer";
import {
  ImageLayer,
  type ImageLayerProps,
  MultiscaleImageLayer,
  type MultiscaleImageLayerProps,
} from "./layers/viv-layers";

export interface ViewState {
  zoom: number;
  target: [number, number];
}

interface BaseConfig {
  source: string | zarr.Readable;
  axis_labels?: string[];
  name?: string;
  colormap?: string;
  opacity?: number;
  acquisition?: string;
  model_matrix?: string | number[];
  onClick?: (e: unknown) => void;
  /**
   * Disable the default click-to-open links on plate/well grids (opening an
   * individual well or field in a new window). A custom `onClick` handler, if
   * provided, is still invoked. Accepts a boolean or a URL query string.
   */
  disable_well_links?: boolean | string;
  /**
   * Show a scale bar derived from the OME-NGFF axis units and scale transforms
   * (default: true). Falls back to base-resolution pixels when there is no unit.
   * Accepts a boolean or a URL query string. Read from the first image added.
   */
  scalebar?: boolean | string;
  /**
   * Show an info overlay with the image/plate name and, once zoomed in on a
   * plate or well, the well (and field) under the center of the view
   * (default: true). Accepts a boolean or a URL query string.
   */
  overlay?: boolean | string;
  /** Optional free-text description shown in the info overlay. */
  description?: string;
}

export interface MultichannelConfig extends BaseConfig {
  colors?: string[];
  channel_axis?: number;
  contrast_limits?: [min: number, max: number][];
  names?: string[];
  visibilities?: boolean[];
}

export interface SingleChannelConfig extends BaseConfig {
  color?: string;
  contrast_limits?: [min: number, max: number];
  visibility?: boolean;
}

export type ImageLayerConfig = MultichannelConfig | SingleChannelConfig;

export type OnClickData = Record<string, unknown> & {
  gridCoord?: { row: number; column: number };
};

export type ImageLabels = Array<{
  name: string;
  loader: ZarrPixelSource[];
  modelMatrix: Matrix4;
  colors?: ReadonlyArray<OmeColor>;
}>;

export type SourceData = {
  loader: ZarrPixelSource[];
  loaders?: GridLoader[]; // for OME plates
  rows?: number;
  columns?: number;
  acquisitions?: Ome.Acquisition[];
  acquisitionId?: number;
  name?: string;
  channel_axis: number | null;
  colors: string[];
  names: string[];
  contrast_limits: ([min: number, max: number] | undefined)[];
  visibilities: boolean[];
  defaults: {
    selection: number[];
    colormap: string;
    opacity: number;
  };
  model_matrix: Matrix4;
  axis_labels: string[];
  onClick?: (e: OnClickData) => void;
  labels?: ImageLabels;
  /** Physical size of one base-resolution pixel along x, if the metadata has units. */
  pixel_size?: { size: number; sizeY?: number; unit: string };
  /** Label for grid cells in the info overlay, e.g. "Well" (plate) or "Field" (well). */
  cell_label?: string;
  /** Well name (e.g. "B03") when the source is a single well, whose grid cells are its fields. */
  well?: string;
  /** Info overlay & scale bar settings, resolved from the config in `addImageAtom`. */
  display?: { scalebar: boolean; overlay: boolean; description?: string };
};

type LayerType = "image" | "multiscale" | "grid";
type LayerPropsMap = {
  image: ImageLayerProps;
  multiscale: MultiscaleImageLayerProps;
  grid: GridLayerProps;
};

export type LayerState<T extends LayerType = LayerType> = {
  kind: T;
  layerProps: LayerPropsMap[T];
  on: boolean;
  labels?: Array<{
    layerProps: Omit<LabelLayerProps, "selection">;
    on: boolean;
    transformSourceSelection: (sourceSelection: Array<number>) => Array<number>;
  }>;
};

type WithId<T> = T & { id: string };

export const viewStateAtom = atom<ViewState | null>(null);
/** Size of the deck.gl canvas in screen pixels, once it has been laid out. */
export const viewportSizeAtom = atom<{ width: number; height: number } | null>(null);
export const sourceErrorAtom = atom<string | null>(null);

export interface Redirect {
  url: string;
  message: string;
}
export const redirectObjAtom = atom<Redirect | null>(null);

export const sourceInfoAtom = atom<WithId<SourceData>[]>([]);

export const addImageAtom = atom(null, async (get, set, config: ImageLayerConfig) => {
  const id = Math.random().toString(36).slice(2);

  try {
    const sourceData = await createSourceData(config);
    sourceData.display = {
      scalebar: config.scalebar === undefined || coerceBoolean(config.scalebar),
      overlay: config.overlay === undefined || coerceBoolean(config.overlay),
      description: config.description,
    };
    const prevSourceInfo = get(sourceInfoAtom);
    if (!sourceData.name) {
      sourceData.name = `image_${Object.keys(prevSourceInfo).length}`;
    }
    set(sourceInfoAtom, [...prevSourceInfo, { id, ...sourceData }]);
  } catch (err) {
    rethrowUnless(err, Error);
    if (err instanceof RedirectError) {
      set(redirectObjAtom, { message: err.message, url: err.url });
    } else {
      set(sourceErrorAtom, err.message);
    }
  }
});

export const sourceInfoAtomAtoms = splitAtom(sourceInfoAtom);

/**
 * Sets the index of named non-channel axes (e.g. `{ t: 3, z: 10 }`) on every layer
 * that has them, for all of its channels. Indices are clamped to the axis size.
 */
export const setAxisSelectionAtom = atom(null, (get, set, update: Record<string, number>) => {
  for (const source of get(sourceInfoAtom)) {
    const { shape } = source.loader[0];
    const changes: Array<[axis: number, index: number]> = [];
    for (const [name, index] of Object.entries(update)) {
      const axis = source.axis_labels.indexOf(name);
      if (axis < 0 || axis === source.channel_axis || name === "x" || name === "y") continue;
      changes.push([axis, Math.min(Math.max(Math.round(index), 0), shape[axis] - 1)]);
    }
    if (changes.length === 0) continue;
    const layerAtom = layerFamilyAtom(source);
    const layer = get(layerAtom);
    const selections = layer.layerProps.selections.map((selection) => {
      const next = [...selection];
      for (const [axis, index] of changes) next[axis] = index;
      return next;
    });
    set(layerAtom, { ...layer, layerProps: { ...layer.layerProps, selections } } as typeof layer);
  }
});

export const layerFamilyAtom: AtomFamily<WithId<SourceData>, PrimitiveAtom<WithId<LayerState>>> = atomFamily(
  (param: WithId<SourceData>) => atom({ ...initLayerStateFromSource(param), id: param.id }),
  (a, b) => a.id === b.id,
);

export type VizarrLayer =
  | Layer<MultiscaleImageLayerProps>
  | Layer<ImageLayerProps>
  | Layer<GridLayerProps>
  | Layer<LabelLayerProps>;

const LayerConstructors = {
  image: ImageLayer,
  multiscale: MultiscaleImageLayer,
  grid: GridLayer,
} as const;

const layerInstanceFamily = atomFamily((a: Atom<LayerState>) =>
  atom((get) => {
    const { on, layerProps, kind, labels } = get(a);
    if (!on) {
      return null;
    }
    if (kind === "grid") {
      // Grids draw each cell's labels themselves, positioned like the cell.
      // @ts-expect-error - deck types `loaders` as its own Loader[] (see LayerConstructors below)
      return new GridLayer({
        ...(layerProps as GridLayerProps),
        labels: labels?.map((label) =>
          label.on
            ? {
                selection: label.transformSourceSelection(layerProps.selections[0]),
                opacity: label.layerProps.opacity,
                colors: label.layerProps.colors,
              }
            : null,
        ),
      }) as VizarrLayer;
    }
    const Layer = LayerConstructors[kind];
    // @ts-expect-error - TS can't resolve that Layer & layerProps bound together
    return new Layer(layerProps) as VizarrLayer;
  }),
);

const imageLabelsIstanceFamily = atomFamily((a: Atom<LayerState>) =>
  atom((get) => {
    const { on, labels, layerProps, kind } = get(a);
    if (!on || !labels || kind === "grid") {
      return [];
    }
    return labels.map((label) =>
      label.on
        ? new LabelLayer({
            ...label.layerProps,
            selection: label.transformSourceSelection(layerProps.selections[0]),
          })
        : null,
    );
  }),
);

export const layerAtoms = atom((get) => {
  const layerAtoms = [];
  for (const sourceAtom of get(sourceInfoAtomAtoms)) {
    const layerStateAtom = layerFamilyAtom(get(sourceAtom));
    layerAtoms.push(layerInstanceFamily(layerStateAtom));
    layerAtoms.push(imageLabelsIstanceFamily(layerStateAtom));
  }
  return get(waitForAll(layerAtoms)).flat();
});
